import { ref } from 'vue'
import axiosInstance from '@/axios'

/**
 * useHideUnpreviewableColors -- shared read of the SuperAdmin
 * ``hide_unpreviewable_color_variants`` platform-settings toggle.
 *
 * The product editor's two color surfaces (ColorsPanel + SupplierSkuPanel)
 * must agree on ONE boolean: does an Artifi-unpreviewable color get HIDDEN
 * (toggle ON, the launch default) or SHOWN-DIMMED/FLAGGED (toggle OFF).
 * This composable is the single shared read so both panels use the same
 * value AND only one GET fires per page load (module-level cache +
 * in-flight dedupe, mirroring useArtifiCoverage's pattern).
 *
 * Fail-SAFE default: ``hideUnpreviewable`` reads TRUE while the fetch is in
 * flight AND if the fetch errors or returns a non-boolean payload. This
 * matches the backend's unset default (also TRUE) and the launch behavior
 * (hide). A transient read failure must never surprise-REVEAL colors that
 * were meant to be hidden -- the safe direction on uncertainty is to keep
 * hiding, not to expose colors an admin has not opted to reveal.
 *
 * Public surface:
 *
 *   const { hideUnpreviewable, loading, error } = useHideUnpreviewableColors()
 *
 *   hideUnpreviewable: Ref<boolean>            // TRUE = hide, FALSE = dim/flag
 *   loading:           Ref<boolean>            // request in flight
 *   error:             Ref<Error|null>         // any fetch error (fails safe)
 */
// Use the /api-prefixed form to match the peer call sites for this SAME
// endpoint (PlatformSettings.vue) and the other platform-settings/Artifi
// reads. The bare form resolves only because the axios interceptor strips a
// leading /api when baseURL ends in /api -- the prefixed form does not depend
// on that, matching the established convention.
const ENDPOINT = '/api/platform-settings/hide-unpreviewable-color-variants'

// Module-level shared state: one fetch per page load, shared across every
// caller. ``_value`` holds the last resolved boolean (or the TRUE fail-safe
// default until/unless a real fetch resolves it).
const _value = ref(true)
const _loading = ref(false)
const _error = ref(null)
let _inflight = null
let _resolved = false

function _fetchOnce() {
    if (_resolved) {
        return Promise.resolve(_value.value)
    }
    if (_inflight) {
        return _inflight
    }
    _loading.value = true
    _error.value = null
    _inflight = axiosInstance
        .get(ENDPOINT)
        .then((resp) => {
            const raw = resp && resp.data ? resp.data.value : null
            // Only a real boolean overrides the fail-safe default. Any other
            // shape (missing key, string, number, null) leaves it TRUE.
            if (typeof raw === 'boolean') {
                _value.value = raw
            } else {
                _value.value = true
            }
            _resolved = true
            return _value.value
        })
        .catch((err) => {
            // Fail SAFE: keep hiding on a read error. Do NOT mark resolved
            // so a later mount can retry the fetch.
            _error.value = err
            _value.value = true
            return _value.value
        })
        .finally(() => {
            _loading.value = false
            _inflight = null
        })
    return _inflight
}

/**
 * Test-only: reset the module-level shared state so specs re-isolate per
 * test without a prior fetch bleeding across cases.
 */
export function _resetHideUnpreviewableCache() {
    _value.value = true
    _loading.value = false
    _error.value = null
    _inflight = null
    _resolved = false
}

export function useHideUnpreviewableColors() {
    // Kick the shared fetch on first use; subsequent callers reuse it.
    _fetchOnce()
    return {
        hideUnpreviewable: _value,
        loading: _loading,
        error: _error,
    }
}

export default useHideUnpreviewableColors
